/**
 * MODULE: _shared/frontend-shell/src/hooks/useGoldenCrmOpportunities.ts
 * PURPOSE: Dashboard and shell analytics — deal list from GET /api/v1/crm/deals (golden CRM path)
 * PUBLIC_API: useGoldenCrmOpportunities, GoldenCrmOpportunitiesFilters, shouldRetryGoldenCrmQuery
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-04 [2.2.1273] — **`GOLDEN_CRM_OPPORTUNITIES_QUERY_KEY`** from **`goldenCrmQuerySync`** (Vite **`@shared`** shim).
 * RECENT_PRIOR: 2026-06-04 [2.2.1272] — useApiAuthReady gates queries; 401 stops retry via shouldRetryGoldenCrmQuery.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { Opportunity } from '@domain/sales-opportunities/contracts/types';
import { CrmHttpError } from '@shared/lib/frontend/api/fetchWithAuthRetry';
import { crmList } from '@shared/lib/frontend/api/crmApi';
import { GOLDEN_CRM_OPPORTUNITIES_QUERY_KEY } from '@shared/lib/frontend/api/goldenCrmQuerySync';
import { mapCrmDealRowToDashboardOpportunity } from './goldenCrmOpportunityMapper';
import { useApiAuthReady } from './useApiAuthReady';

export type GoldenCrmOpportunitiesFilters = {
  workspace_id?: string;
  stage?: string;
  /** When `active`, excludes closed_won and closed_lost (matches legacy sales-opportunities list). */
  status?: string;
};

export function shouldRetryGoldenCrmQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof CrmHttpError && error.status === 401) return false;
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401 || status === 429) return false;
  if (!error || typeof error !== 'object') return failureCount < 2;
  const e = error as { code?: string; message?: string };
  if (e.code === 'ERR_NETWORK' || e.code === 'ERR_NAME_NOT_RESOLVED') return false;
  const msg = String(e.message ?? '');
  if (msg.includes('Failed to fetch') || msg.includes('Network Error')) return false;
  return failureCount < 2;
}

async function fetchGoldenCrmOpportunities(
  filters?: GoldenCrmOpportunitiesFilters
): Promise<Opportunity[]> {
  const params: Record<string, string | number | undefined> = { limit: 500 };
  if (filters?.stage) params.stage = filters.stage;
  const res = await crmList<Record<string, unknown>>('deals', params);
  let items = (res.items ?? []).map((row) => mapCrmDealRowToDashboardOpportunity(row));
  if (filters?.status === 'active') {
    items = items.filter((o) => o.status === 'active');
  }
  return items;
}

/** Drop-in replacement for `useOpportunities` on surfaces that should read golden CRM deals. */
export function useGoldenCrmOpportunities(
  filters?: GoldenCrmOpportunitiesFilters,
  options?: Pick<
    UseQueryOptions<Opportunity[], Error, Opportunity[], readonly unknown[]>,
    'enabled' | 'refetchInterval' | 'refetchIntervalInBackground'
  >
) {
  const apiReady = useApiAuthReady();
  const enabled = apiReady && (options?.enabled ?? true);
  return useQuery({
    queryKey: [...GOLDEN_CRM_OPPORTUNITIES_QUERY_KEY, filters],
    queryFn: () => fetchGoldenCrmOpportunities(filters),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: shouldRetryGoldenCrmQuery,
    ...(options ?? {}),
    enabled,
  });
}
